import React, { useState } from "react";
import { convertTo12Hour, formatDateDisplay } from "./bookingStepperUtils";

const fmt = (n) => Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The booking at a glance, beside every step: who, what, when, with whom and the running
// total. Read-only - everything here is edited in its own step.
function SummaryPanel({ b, formik }) {
  const {
    crewLabel,
    resourceLabel,
    currency,
    taxPercentage,
    pricing,
    serviceData,
    lineQty,
    isEditing,
    providerSelected,
    dateSelect,
    timeSelect,
    providerOptional,
    slotOverrideNote,
    showResourceField,
    activeResources,
    resourceIds,
    resourceCurrentName,
    paidAmount,
    balanceDue,
    user_id,
    display_booking_id
  } = b;
  const { values } = formik;
  // On a phone the summary is a bar (with the total) that opens on tap; beside the form it is always open.
  const [mobileOpen, setMobileOpen] = useState(false);

  const added = (serviceData || []).filter((s) => s.added);
  const resourceName = activeResources.find((r) => String(r.id) === String(resourceIds))?.name || (resourceIds ? resourceCurrentName : "");
  const crew = [...new Set((providerSelected || []).map((p) => p?.extendedProps?.search || p?.title).filter(Boolean))];
  const when = dateSelect ? [formatDateDisplay(dateSelect), convertTo12Hour(timeSelect)].filter(Boolean).join(" · ") : "";

  return (
    <div className="bstep-summary" aria-label="Booking summary">
      <button type="button" className="bstep-summary-toggle" aria-expanded={mobileOpen} onClick={() => setMobileOpen((v) => !v)}>
        <span>Summary</span>
        <span>
          {pricing.hasContent ? `${currency} ${fmt(pricing.total)}` : "—"} {mobileOpen ? "▲" : "▼"}
        </span>
      </button>
      <div className={`bstep-summary-body ${mobileOpen ? "is-open" : ""}`}>
        <div className="bstep-summary-title">
          Summary
          {isEditing && display_booking_id ? <span className="bstep-muted"> · {display_booking_id}</span> : null}
        </div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">Customer</div>
          {values.userName || values.phoneNumber ? (
            <>
              <div className="bstep-sum-value">
                {values.userName || "—"}
                {user_id ? <span className="bstep-chip is-found ms-2">Existing</span> : null}
              </div>
              {values.phoneNumber && <div className="bstep-muted">+{values.phoneNumber}</div>}
            </>
          ) : (
            <div className="bstep-muted">Not entered yet</div>
          )}
        </div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">Services</div>
          {added.length === 0 ? (
            <div className="bstep-muted">Nothing chosen yet</div>
          ) : (
            added.map((s) => {
              const qty = isEditing ? 1 : Math.max(1, Math.floor(Number(lineQty[String(s.service_offering_id)])) || 1);
              return (
                <div key={s.service_offering_id} className="bstep-sum-line">
                  <span>
                    {s.service_offering_name}
                    {qty > 1 ? ` × ${qty}` : ""}
                  </span>
                  <span>
                    {currency} {fmt((Number(s.amount) || 0) * qty)}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">When</div>
          {when ? <div className="bstep-sum-value">{when}</div> : <div className="bstep-muted">Not chosen yet</div>}
          {providerOptional && slotOverrideNote && (
            <div className="bstep-slot-note is-warning" role="alert" data-testid="summary-slot-note">
              {slotOverrideNote}
            </div>
          )}
          <div className="bstep-sum-label mt-2">{crewLabel}</div>
          {crew.length ? (
            <div className="bstep-sum-chips">
              {crew.map((n) => (
                <span key={n} className="bstep-crew-chip">
                  {n}
                </span>
              ))}
            </div>
          ) : (
            <div className="bstep-muted">{providerOptional ? "To be assigned later" : "Nobody selected yet"}</div>
          )}
          {showResourceField && resourceName && (
            <>
              <div className="bstep-sum-label mt-2">{resourceLabel}</div>
              <div className="bstep-sum-value">{resourceName}</div>
            </>
          )}
        </div>

        <div className="bstep-sum-block bstep-sum-money">
          {pricing.hasContent ? (
            <>
              <div className="bstep-sum-line">
                <span>Subtotal</span>
                <span>
                  {currency} {fmt(pricing.subtotal)}
                </span>
              </div>
              {pricing.discountAmount > 0 && (
                <div className="bstep-sum-line text-success">
                  <span>Discount</span>
                  <span>
                    −{currency} {fmt(pricing.discountAmount)}
                  </span>
                </div>
              )}
              {pricing.taxAmount > 0 && (
                <div className="bstep-sum-line">
                  <span>Tax ({taxPercentage}%)</span>
                  <span>
                    {currency} {fmt(pricing.taxAmount)}
                  </span>
                </div>
              )}
              {pricing.tipAmount > 0 && (
                <div className="bstep-sum-line">
                  <span>Tip</span>
                  <span>
                    {currency} {fmt(pricing.tipAmount)}
                  </span>
                </div>
              )}
              <div className="bstep-sum-line is-total">
                <span>Total</span>
                <span>
                  {currency} {fmt(pricing.total)}
                </span>
              </div>
              {Number(paidAmount) > 0 && (
                <div className="bstep-sum-line">
                  <span>Paid</span>
                  <span>
                    {currency} {fmt(paidAmount)}
                  </span>
                </div>
              )}
              {Number(paidAmount) > 0 && (
                <div className="bstep-sum-line">
                  <span>Balance due</span>
                  <span>
                    {currency} {fmt(balanceDue > 0 ? balanceDue : 0)}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="bstep-muted">The total appears once a service is chosen.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SummaryPanel;
